const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mRadioButtonGroup.tsx';
import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps, RadioButtonGroupSubheader, RadioButtonOptionEntry } from '../types';
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Icon,
    IconButton,
    Radio,
    RadioGroup,
    Typography,
    TypographyProps,
} from '@mui/material';
import { mapObjectToOption, mapOptionToObject } from '../utils/data';
import { css } from '@emotion/css';
import { HelpDialog } from '../../../components/Forms/components/HelpDialog/HelpDialog';
import { TextInput, useOptionalText } from '../../../utils/intlUtils';
import { useIntl } from 'react-intl';

export type RadioButtonGroupItem = RadioButtonOptionEntry | RadioButtonGroupSubheader;

interface Props extends CIS4mValueComponentProps {
    label?: TextInput;
    options: RadioButtonGroupItem[];
    required?: boolean;
    row?: boolean;
    storeValueAsObject?: boolean;
    checkValidity?: () => string;
    infoText?: string;
}

const cssHelpTextButton = css`
    margin-left: 8px;
    padding: 4px;
`;

const sxTypography: TypographyProps['sx'] = {
    width: '100%',
    mt: 1,
    mb: 0.5,
};

export const CIS4mRadioButtonGroup = forwardRef((props: Props, ref: any) => {
    const { formatMessage } = useIntl();

    const { path, initialValue, options: items, checkValidity, row } = props;

    const label = useOptionalText(props.label);
    const hidden = props.hidden ?? false;
    const required = props.required ?? false;
    const disabled = props.disabled ?? false;
    const storeValueAsObject = props.storeValueAsObject ?? true;

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity,
        disabled,
        hidden,
        initialValue: storeValueAsObject ? mapOptionToObject(initialValue) : initialValue,
        path,
        required,
    });

    const [showInfoDialog, setShowInfoDialog] = React.useState(false);

    const handleInfoDialogButtonClick = () => setShowInfoDialog(true);
    const handleCloseInfoDialog = () => setShowInfoDialog(false);

    const renderInfoTextStuff = () => {
        return (
            <>
                <IconButton onClick={handleInfoDialogButtonClick} size="large" className={cssHelpTextButton}>
                    <Icon className={`icons8-info`} />
                </IconButton>
                <HelpDialog
                    title={label || ''}
                    text={props.infoText}
                    isOpen={showInfoDialog}
                    onClose={handleCloseInfoDialog}
                />
            </>
        );
    };

    const options = useMemo(
        () =>
            items.filter(item => {
                return 'value' in item && 'label' in item;
            }) as RadioButtonOptionEntry[],
        [items]
    );

    const renderSubheader = (subheaderItem: RadioButtonGroupSubheader) => {
        const { subheader } = subheaderItem;
        const label = typeof subheader === 'string' ? subheader : formatMessage(subheader);
        const key = `sh-${label}`;
        return (
            <Typography key={key} variant="body1" component="div" sx={sxTypography}>
                {label}
            </Typography>
        );
    };

    const renderOption = (option: RadioButtonOptionEntry, index: number) => {
        const label = option.img ? <img src={option.img} alt={option.img} loading="lazy" /> : option.label;
        return <FormControlLabel key={index} value={option.value} control={<Radio size={'small'} />} label={label} />;
    };

    const itemElements = items.map((item, id: number) => {
        if ('subheader' in item) {
            return renderSubheader(item as RadioButtonGroupSubheader);
        } else {
            return renderOption(item as RadioButtonOptionEntry, id);
        }
    });

    const labelElement = label ? (
        <div>
            <FormLabel id={'mobile-radio-group-label'}>{label}</FormLabel>
            {props.infoText ? renderInfoTextStuff() : null}
        </div>
    ) : null;

    const handleRadioGroupChange = (event: any) => {
        const { value } = event.target;
        setValue(storeValueAsObject ? mapOptionToObject(value) : value);
    };

    const radioGroupValue = storeValueAsObject ? mapObjectToOption(value, options) : value;

    return hidden ? null : (
        <FormControl required={required} error={!!errorMessage} disabled={disabled}>
            {labelElement}
            <RadioGroup
                name={'mobile-radio-group'}
                aria-labelledby={'mobile-radio-group-label'}
                row={row}
                value={radioGroupValue}
                onChange={handleRadioGroupChange}
            >
                {itemElements}
            </RadioGroup>
            {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
    );
});

CIS4mRadioButtonGroup.displayName = 'CIS4mRadioButtonGroup';
